import * as React from 'react';
import {Redirect, RouteComponentProps} from 'react-router-dom';
import {Loader} from '@flexe/ui-components';
import DatePicker from 'react-datepicker';
import {useReducer} from 'react';
import DocumentsService from '../../shared/services/DocumentsService';
import OutboundManifestService from '../../shared/services/OutboundManifestService';
import {ResponseError, Warehouse} from '../../shared/CommonInterfaces';
import {CompanyType} from '../../shared/CommonInterfaces';
import FlexeContext from '../../contexts/FlexeContext';
import InlineEdit from '../../shared/InlineEdit';
import StagingLocationTypeAhead from '../staging-location/StagingLocationTypeAhead';
import {FreightShipMode, ParcelShipMode} from '../../shared/constants';
import WarehouseService from '../../shared/services/WarehouseService';
import {
  Destination,
  FilterKeys,
  FilterParams,
  GetShipmentsParams,
  Shipment,
  SortByColumns
} from '../outbound-shipments/ShipmentInterfaces';
import {SortableColumnNameToSortByColumnMap} from '../outbound-shipments/ShipmentConstants';
import ShipmentTable from '../outbound-shipments/shipment-list/ShipmentTable';
import {SortState} from '../outbound-shipments/shipment-list/ShipmentTableHeader';
import {SortOrder} from '../../shared/CommonInterfaces';
import OutboundShipmentService from '../../shared/services/OutboundShipmentService';
import LocationsService from '../../locations/LocationsService';
import FileDownloadLink from '../../shared/FileDownloadLink';
import PaginationButtons from '../outbound-shipments/shipment-list/PaginationButtons';
import {usePrevious, valueIsNotUndefined} from '../../shared/ReactHelpers';
import ShipmentFilter from '../outbound-shipments/shipment-list/ShipmentFilter';
import {ShipmentDocuments} from '../../documents/ShipmentDocuments';
import OutboundFreightLoadService from '../../shared/services/OutboundFreightLoadService';
import {FreightLoad, FreightLoadStop} from '../outbound-freight/FreightInterfaces';
import ManifestContentsService from '../../shared/services/ManifestContentsService';
import {ShippingInfo} from '../outbound-shipments/shipment-details/ShippingInfo';
import formatTimeWithTimezone from '../../shared/utilities/formatTimeWithTimezone';
import CancelLoadModal from './CancelLoadModal';
import {ManifestStatus, OutboundTrailerManifestDetail} from './ManifestInterfaces';
import ManifestDetailActionButtons from './ManfestDetailActionButtons';
import CompleteManifestModal from './CompleteManifestModal';
import {ManifestContents} from './ManifestContents';
import {ManifestContentsSearchByManifestRequest, ManifestLpn} from './ManifestInterfaces';

interface State {
  manifest?: OutboundTrailerManifestDetail;
  isLoading: boolean;
  loadError: boolean;
  otherError: boolean;
  errors: ResponseError[];
  editingTrailerId: boolean;
  editingScac: boolean;
  editingLoadingLocation: boolean;
  redirectPath: string;
}

const manifestListPath = '/wh/outbound_manifests';

enum types {
  MANIFEST_LOADED,
  LOADED,
  LOAD_FAILED,
  REMOVE_SHIPMENT_COMPLETE,
  REMOVE_SHIPMENT_ERROR,
  CLOSE_AND_SUBMIT_COMPLETE,
  CLOSE_AND_SUBMIT_ERROR,
  TOGGLE_EDIT_TRAILER_ID,
  TOGGLE_EDIT_SCAC,
  TOGGLE_EDIT_LOADING_LOCATION,
  UPDATE_TRAILER_ID_COMPLETE,
  UPDATE_SCAC_COMPLETE,
  UPDATE_LOADING_LOCATION_COMPLETE,
  UPDATE_TRAILER_ID_ERROR,
  UPDATE_SCAC_ERROR,
  UPDATE_LOADING_LOCATION_ERROR,
  UPDATE_FREIGHT_LOAD_ERROR,
  CANCEL_COMPLETE,
  CANCEL_ERROR,
  DOWNLOAD_COMPLETE,
  DOWNLOAD_ERROR,
  REGENERATE_BOL_ERROR
}

type Action =
  | {type: types.MANIFEST_LOADED; results: OutboundTrailerManifestDetail}
  | {type: types.LOADED}
  | {type: types.LOAD_FAILED; errors: ResponseError[]}
  | {
      type: types.REMOVE_SHIPMENT_COMPLETE;
      results: OutboundTrailerManifestDetail;
    }
  | {type: types.REMOVE_SHIPMENT_ERROR; errors: ResponseError[]}
  | {type: types.CLOSE_AND_SUBMIT_COMPLETE}
  | {type: types.CLOSE_AND_SUBMIT_ERROR; errors: ResponseError[]}
  | {type: types.TOGGLE_EDIT_TRAILER_ID}
  | {type: types.TOGGLE_EDIT_SCAC}
  | {type: types.TOGGLE_EDIT_LOADING_LOCATION}
  | {
      type: types.UPDATE_TRAILER_ID_COMPLETE;
      results: OutboundTrailerManifestDetail;
    }
  | {
      type: types.UPDATE_SCAC_COMPLETE;
      results: OutboundTrailerManifestDetail;
    }
  | {
      type: types.UPDATE_LOADING_LOCATION_COMPLETE;
      results: OutboundTrailerManifestDetail;
    }
  | {type: types.UPDATE_TRAILER_ID_ERROR; errors: ResponseError[]}
  | {type: types.UPDATE_SCAC_ERROR; errors: ResponseError[]}
  | {type: types.UPDATE_FREIGHT_LOAD_ERROR; errors: ResponseError[]}
  | {type: types.UPDATE_LOADING_LOCATION_ERROR; errors: ResponseError[]}
  | {type: types.CANCEL_COMPLETE}
  | {type: types.CANCEL_ERROR; errors: ResponseError[]}
  | {type: types.DOWNLOAD_COMPLETE}
  | {type: types.DOWNLOAD_ERROR; errors: ResponseError[]}
  | {type: types.REGENERATE_BOL_ERROR; errors: ResponseError[]};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case types.LOADED: {
      return {
        ...state,
        isLoading: false,
        otherError: false,
        editingTrailerId: false
      };
    }

    case types.MANIFEST_LOADED: {
      return {
        ...state,
        manifest: action.results
      };
    }

    case types.LOAD_FAILED: {
      return {
        ...state,
        isLoading: false,
        loadError: true,
        errors: action.errors
      };
    }

    case types.REMOVE_SHIPMENT_COMPLETE: {
      return {
        ...state,
        isLoading: false,
        otherError: false,
        manifest: action.results
      };
    }

    case types.REMOVE_SHIPMENT_ERROR: {
      return {
        ...state,
        isLoading: false,
        otherError: true,
        errors: action.errors
      };
    }

    case types.CLOSE_AND_SUBMIT_COMPLETE: {
      return {
        ...state,
        isLoading: false,
        loadError: false
      };
    }

    case types.CLOSE_AND_SUBMIT_ERROR: {
      return {
        ...state,
        isLoading: false,
        otherError: true,
        loadError: false,
        errors: action.errors
      };
    }

    case types.TOGGLE_EDIT_TRAILER_ID: {
      return {
        ...state,
        editingTrailerId: !state.editingTrailerId
      };
    }

    case types.TOGGLE_EDIT_SCAC: {
      return {
        ...state,
        editingScac: !state.editingScac
      };
    }

    case types.TOGGLE_EDIT_LOADING_LOCATION: {
      return {
        ...state,
        editingLoadingLocation: !state.editingLoadingLocation
      };
    }

    case types.UPDATE_TRAILER_ID_COMPLETE: {
      return {
        ...state,
        isLoading: false,
        otherError: false,
        editingTrailerId: false,
        manifest: action.results
      };
    }

    case types.UPDATE_SCAC_COMPLETE: {
      return {
        ...state,
        isLoading: false,
        otherError: false,
        editingScac: false,
        manifest: action.results
      };
    }

    case types.UPDATE_LOADING_LOCATION_COMPLETE: {
      return {
        ...state,
        isLoading: false,
        otherError: false,
        editingLoadingLocation: false,
        manifest: action.results
      };
    }

    case types.UPDATE_TRAILER_ID_ERROR:
    case types.UPDATE_SCAC_ERROR:
    case types.UPDATE_FREIGHT_LOAD_ERROR:
    case types.UPDATE_LOADING_LOCATION_ERROR:
    case types.REGENERATE_BOL_ERROR: {
      return {
        ...state,
        isLoading: false,
        otherError: true,
        errors: action.errors
      };
    }

    case types.CANCEL_COMPLETE: {
      return {
        ...state,
        isLoading: false,
        otherError: false,
        redirectPath: manifestListPath
      };
    }

    case types.CANCEL_ERROR: {
      return {
        ...state,
        isLoading: false,
        otherError: true,
        errors: action.errors
      };
    }

    case types.DOWNLOAD_COMPLETE: {
      return {
        ...state,
        isLoading: false,
        otherError: false
      };
    }

    case types.DOWNLOAD_ERROR: {
      return {
        ...state,
        isLoading: false,
        otherError: true,
        errors: action.errors
      };
    }
  }
}

interface ManifestDetailProps extends RouteComponentProps<{id: string}> {
  selectedWarehouse: Warehouse;
}

const ManifestDetail: React.FC<ManifestDetailProps> = (props) => {
  const {authenticityToken} = React.useContext(FlexeContext);
  const [shipmentsForDisplay, setShipmentsForDisplay] = React.useState<Shipment[]>([]);
  const [shipmentsCount, setShipmentsCount] = React.useState<number>(0);
  const [currentManifestLpnsPage, setCurrentManifestLpnsPage] = React.useState<number>(1);
  const [manifestLpnsContinuationTokens, setManifestLpnsContinuationTokens] = React.useState({1: null});
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [continuationTokens, setContinuationTokens] = React.useState({1: null});
  const [reloadShipments, setReloadShipments] = React.useState<boolean>(false);
  const [reloadManifest, setReloadManifest] = React.useState<boolean>(false);
  const [sortColumn, setSortColumn] = React.useState<SortByColumns>(SortByColumns.ShipmentId);
  const [sortOrder, setSortOrder] = React.useState<SortOrder>(SortOrder.DESC);
  const [shipmentsLoading, setShipmentsLoading] = React.useState<boolean>(true);
  const [filterParams, setFilterParams] = React.useState<FilterParams>({});
  const [completingManifest, setCompletingManifest] = React.useState<boolean>(false);
  const [isCancelModalOpen, toggleIsCancelModalOpen] = useReducer((isOpen) => !isOpen, false);
  const [cancellingManifest, setCancellingManifest] = React.useState<boolean>(false);
  const [editingProNumber, setEditingProNumber] = React.useState<boolean>(false);
  const [editingSealNumber, setEditingSealNumber] = React.useState<boolean>(false);
  const [editingShipBy, setEditingShipBy] = React.useState<boolean>(false);
  const [editingFreight, setEditingFreight] = React.useState<boolean>(false);
  const [freightLoad, setfreightLoad] = React.useState<FreightLoad>(null);
  const [freightLoadStop, setfreightLoadStop] = React.useState<FreightLoadStop>(null);
  const [manifestLpns, setManifestLpns] = React.useState<ManifestLpn[]>(null);
  const [manifestLpnsCount, setManifestLpnsCount] = React.useState<number>(0);
  const [reservationId, setReservationId] = React.useState<number>(null);
  const [refreshLpns, setRefreshLpns] = React.useState<boolean>(false);
  const [showCompletedModal, setShowCompletedModal] = React.useState<boolean>(false);
  const [scac, setScac] = React.useState<string>(null);
  const [proNumber, setProNumber] = React.useState<string>(null);
  const [trailerNumber, setTrailerNumber] = React.useState<string>(null);
  const [sealNumber, setSealNumber] = React.useState<string>(null);
  const [destination, setDestination] = React.useState<Destination>(null);
  const [billOfLadingIsGenerating, setBillOfLadingIsGenerating] = React.useState<boolean>(false);
  const [showNumberOfShipments, setShowNumberOfShipments] = React.useState(false);

  const outboundManifestService = new OutboundManifestService(authenticityToken);
  const outboundFreightLoadService = new OutboundFreightLoadService(authenticityToken);
  const manifestContentsService = new ManifestContentsService(authenticityToken);
  const locationsService = new LocationsService(authenticityToken);
  const warehouseService = new WarehouseService(authenticityToken);
  const shipmentService = new OutboundShipmentService(authenticityToken);
  const documentsService = new DocumentsService(CompanyType.warehouse);
  const PAGE_SIZE = 50;

  const [state, dispatch] = React.useReducer(reducer, {
    loadError: false,
    isLoading: true,
    otherError: false,
    errors: [],
    editingTrailerId: false,
    editingScac: false,
    editingLoadingLocation: false,
    redirectPath: null
  });

  const prevReloadShipments = usePrevious(reloadShipments);
  React.useEffect(() => {
    if (valueIsNotUndefined(prevReloadShipments)) {
      fetchShipments();
    }
  }, [reloadShipments, filterParams]);

  const prevPage = usePrevious(currentPage);
  React.useEffect(() => {
    if (valueIsNotUndefined(prevPage)) {
      setReloadShipments(!reloadShipments);
    }
  }, [currentPage]);

  const prevSortOrder = usePrevious(sortOrder);
  React.useEffect(() => {
    if (valueIsNotUndefined(prevSortOrder)) {
      resetPagination();
      setReloadShipments(!reloadShipments);
    }
  }, [sortOrder]);

  const prevSortColumn = usePrevious(sortColumn);
  React.useEffect(() => {
    if (valueIsNotUndefined(prevSortColumn)) {
      resetPagination();
      setReloadShipments(!reloadShipments);
    }
  }, [sortColumn]);

  React.useEffect(() => {
    const fetchData = async () => {
      const manifest = await fetchManifest();
      if (freighLoadDetailsRequired(manifest)) {
        await fetchFreightLoad(manifest.freightLoadId);
      }
    };

    const fetchManifest = async () => {
      const manifestId = parseInt(props.match.params.id, 10);
      const response = await outboundManifestService.getManifestDetail(manifestId);
      if (response.data && (!response.errors || response.errors.length === 0)) {
        if (reservationId !== response.data.reservationId) {
          setReservationId(response.data.reservationId);
        }
        dispatch({
          type: types.MANIFEST_LOADED,
          results: response.data
        });
        if (!freighLoadDetailsRequired(response.data)) {
          dispatch({
            type: types.LOADED
          });
        }
      } else {
        dispatch({
          type: types.LOAD_FAILED,
          errors: response.errors
        });
      }

      // Set this here so that the "Complete" button will stay disabled until we've re-fetched the manifest
      // data. After a completion, the re-fetched manifest data will have the txn_state as completed which
      // will hide the buttons. This prevents the user from being able to "double complete"
      setCompletingManifest(false);

      return response.data;
    };

    const fetchFF = async (flag: string) => {
      const response = await warehouseService.getFeatureFlag(flag, props.selectedWarehouse.id);
      if (response.data && (!response.errors || response.errors.length === 0)) {
        if (flag === 'show-number-of-shipment-manifest-details') {
          setShowNumberOfShipments(response.data.value);
        }
      } else {
        dispatch({
          type: types.LOAD_FAILED,
          errors: response.errors
        });
      }
    };
    fetchFF('show-number-of-shipment-manifest-details');
    fetchData();
    fetchShipments();
  }, [authenticityToken, props.match.params.id, reloadManifest]);

  React.useEffect(() => {
    if (cancellingManifest) {
      cancelManifest();
    }
  }, [cancellingManifest]);

  React.useEffect(() => {
    if (state.manifest?.shipMode === FreightShipMode) {
      fetchManifestLpns();
    }
  }, [authenticityToken, reservationId, currentManifestLpnsPage, state.manifest]);

  React.useEffect(() => {
    if (refreshLpns && state.manifest?.shipMode === FreightShipMode) {
      fetchManifestLpns();
    }
  }, [refreshLpns]);

  React.useEffect(() => {
    if (state.manifest?.shipMode === FreightShipMode && destination == null) {
      fetchDestination();
      fetchFreightLoad(state.manifest.freightLoadId);
    }
  }, [manifestLpns]);

  const completeManifest = async () => {
    setCompletingManifest(true);

    //save changes before completing the manifest, but don't complete if the freight load update fails
    if (await onSave()) {
      const manifestId = parseInt(props.match.params.id, 10);
      const response = await outboundManifestService.completeAndSubmitToCarrier(manifestId);

      if (!response.errors || response.errors.length === 0) {
        setBillOfLadingIsGenerating(true);
        setReloadManifest(!reloadManifest);
        dispatch({type: types.CLOSE_AND_SUBMIT_COMPLETE});

        if (freighLoadDetailsRequired(response.data)) {
          await fetchFreightLoad(response.data.freightLoadId);
        }
      } else {
        setReloadManifest(!reloadManifest);
        dispatch({
          type: types.CLOSE_AND_SUBMIT_ERROR,
          errors: response.errors
        });
      }
    }
  };

  //returns 'true' promise if the save(s) succeed
  const onSave = async (): Promise<boolean> => {
    const trailerNumberHasChanged = state.manifest.trailerId !== trailerNumber;
    const scacHasChanged = state.manifest.scacCode !== scac;
    const sealNumberHasChanged = freightLoadStop.sealNumber !== sealNumber;
    const proNumberHasChanged = freightLoadStop.proNumber !== proNumber;
    const changesWereMade = trailerNumberHasChanged || scacHasChanged || sealNumberHasChanged || proNumberHasChanged;

    if (!changesWereMade) {
      setShowCompletedModal(false);
      return true;
    }

    const errs = [];
    try {
      if (trailerNumberHasChanged) {
        await handleEditTrailerId(trailerNumber);
      }

      if (scacHasChanged) {
        await handleEditScac(scac);
      }

      if (sealNumberHasChanged) {
        handleEditSealNumber(sealNumber);
      }

      if (proNumberHasChanged) {
        handleEditProNumber(proNumber);
      }
    } catch (error) {
      errs.push(error.toString());
      return false;
    } finally {
      setShowCompletedModal(false);
    }
    return true;
  };

  const fetchShipments = async () => {
    setShipmentsLoading(true);
    const token = continuationTokens[currentPage];
    const request: GetShipmentsParams = {
      continuation_token: token,
      warehouse_id: props.selectedWarehouse.id,
      outbound_freight_manifest: props.match.params.id,
      sort_by: sortColumn,
      sort_direction: sortOrder,
      page_size: PAGE_SIZE,
      omni_only: false,
      ...filterParams
    };
    const response = await shipmentService.getShipmentsDeprecated(request);
    if (response.data && (!response.errors || response.errors.length === 0)) {
      setShipmentsCount(response.data.counts);
      const newContinuationTokens = {...continuationTokens};
      newContinuationTokens[currentPage + 1] = response.data.continuation_token;
      setContinuationTokens(newContinuationTokens);
      setShipmentsForDisplay(response.data.shipments);
    } else {
      dispatch({
        type: types.LOAD_FAILED,
        errors: response.errors
      });
    }
    setShipmentsLoading(false);
  };

  const fetchManifestLpns = async () => {
    if (!reservationId) {
      setManifestLpnsCount(0);
      setManifestLpns([]);
    } else {
      const token = manifestLpnsContinuationTokens[currentManifestLpnsPage];
      const manifestContentsSearchByManifestRequest: ManifestContentsSearchByManifestRequest = {
        continuationToken: token,
        pageSize: PAGE_SIZE,
        filters: {
          reservationId,
          manifestId: parseInt(props.match.params.id, 10),
          topLevelLpnsOnly: true
        }
      };

      const response = await manifestContentsService.searchManifestContentsByManifest(
        manifestContentsSearchByManifestRequest
      );

      if (response.data && (!response.errors || response.errors.length === 0)) {
        setManifestLpnsCount(response.data.count);
        setManifestLpns(response.data.manifest_lpns);
        const newContinuationTokens = {...manifestLpnsContinuationTokens};
        newContinuationTokens[currentManifestLpnsPage + 1] = response.data.continuation_token;
        setManifestLpnsContinuationTokens(newContinuationTokens);
      } else {
        dispatch({
          type: types.LOAD_FAILED,
          errors: response.errors
        });
      }
    }
    setRefreshLpns(false);
  };

  const fetchFreightLoad = async (freightLoadId: number) => {
    const response = await outboundFreightLoadService.getFreightLoads([freightLoadId]);
    if (!response.data || (response.errors && response.errors.length > 0)) {
      dispatch({
        type: types.LOAD_FAILED,
        errors: response.errors
      });
    }
    if (response.data.freightLoads && response.data.freightLoads.length > 0) {
      const freightLoadResponse = response.data.freightLoads[0];
      setfreightLoad(freightLoadResponse);
      setScac(freightLoadResponse.scac);
      setTrailerNumber(freightLoadResponse.trailerNumber);
      if (response.data.freightLoads[0].loadStops && response.data.freightLoads[0].loadStops.length > 0) {
        const loadsArray = response.data.freightLoads[0].loadStops;
        const loadStop = loadsArray[loadsArray.length - 1];
        setfreightLoadStop(loadStop);
        setProNumber(loadStop.proNumber);
        setSealNumber(loadStop.sealNumber);
      }
    }
    dispatch({
      type: types.LOADED
    });
  };

  const fetchDestination = async () => {
    if (!manifestLpns || manifestLpns.length === 0) {
      return;
    } else {
      const response = await shipmentService.shipmentDetailsDeprecated(manifestLpns[0].shipment_id);

      if (response.data && (!response.errors || response.errors.length === 0)) {
        setDestination(response.data.shipment?.destination);
      } else {
        dispatch({
          type: types.LOAD_FAILED,
          errors: response.errors
        });
      }
    }
    setRefreshLpns(false);
  };

  const shouldAllowEditing = (): boolean => {
    return state.manifest.status === ManifestStatus.open;
  };

  const shouldShowDownloadButton = (currState: State): boolean => {
    if (!currState.manifest) {
      return false;
    }
    if (!currState.manifest.csvEnabled) {
      return false;
    }
    if (currState.manifest.status !== ManifestStatus.completed) {
      return false;
    }
    if (!currState.manifest.shipMode) {
      return false;
    }
    if (currState.manifest.shipMode === ParcelShipMode) {
      return false;
    }

    return true;
  };

  const freighLoadDetailsRequired = (manifest: OutboundTrailerManifestDetail): boolean => {
    return manifest?.freightLoadId && state.manifest?.shipMode === FreightShipMode;
  };

  const removeShipment = async (shipmentId: number) => {
    const manifestId = parseInt(props.match.params.id, 10);
    const response = await outboundManifestService.removeShipment(manifestId, shipmentId);
    if (response.data && (!response.errors || response.errors.length === 0)) {
      setReloadShipments(!reloadShipments);
      dispatch({
        type: types.REMOVE_SHIPMENT_COMPLETE,
        results: response.data
      });
    } else {
      dispatch({
        type: types.REMOVE_SHIPMENT_ERROR,
        errors: response.errors
      });
    }
  };

  const handleSortChanged = (sortState: SortState) => {
    setSortOrder(sortState.direction);
    setSortColumn(SortableColumnNameToSortByColumnMap.get(sortState.columnName));
  };

  const resetPagination = () => {
    setContinuationTokens({1: null});
    setCurrentPage(1);
  };

  const completeAndSubmitToCarrier = async () => {
    const manifestId = parseInt(props.match.params.id, 10);
    const response = await outboundManifestService.completeAndSubmitToCarrier(manifestId);

    if (!response.errors || response.errors.length === 0) {
      setReloadManifest(!reloadManifest);
      dispatch({type: types.CLOSE_AND_SUBMIT_COMPLETE});
    } else {
      setReloadManifest(!reloadManifest);
      dispatch({
        type: types.CLOSE_AND_SUBMIT_ERROR,
        errors: response.errors
      });
    }
  };

  const cancelManifest = async () => {
    const manifestId = parseInt(props.match.params.id, 10);
    const response = await outboundManifestService.cancelManifest(manifestId);
    setCancellingManifest(false);
    if (response && (!response.errors || response.errors.length === 0)) {
      dispatch({
        type: types.CANCEL_COMPLETE
      });
    } else {
      dispatch({
        type: types.CANCEL_ERROR,
        errors: response.errors
      });
    }
  };

  const downloadManifest = async () => {
    const manifestId = parseInt(props.match.params.id, 10);
    const response = await outboundManifestService.downloadManifest(manifestId);
    if (response && (!response.errors || response.errors.length === 0)) {
      dispatch({
        type: types.DOWNLOAD_COMPLETE
      });
    } else {
      dispatch({
        type: types.DOWNLOAD_ERROR,
        errors: response.errors
      });
    }
  };

  const handleEditTrailerId = async (newTrailerId: string) => {
    let editOk = true;
    if (newTrailerId === '' || newTrailerId === null) {
      setTrailerNumber(state.manifest.trailerId);
      dispatch({
        type: types.UPDATE_SCAC_COMPLETE,
        results: state.manifest
      });
      return;
    }
    if (freighLoadDetailsRequired(state.manifest)) {
      editOk = await editFreightLoad(newTrailerId, freightLoad?.scheduledShipDate, freightLoad?.scac);
    }
    const manifestId = parseInt(props.match.params.id, 10);
    const response = await outboundManifestService.updateTrailerLoadingDetails(manifestId, newTrailerId, null, null);
    editOk = editOk && response.data && (!response.errors || response.errors.length === 0);
    if (editOk) {
      dispatch({
        type: types.UPDATE_TRAILER_ID_COMPLETE,
        results: response.data
      });
    } else if (response.errors.length > 0) {
      dispatch({
        type: types.UPDATE_TRAILER_ID_ERROR,
        errors: response.errors
      });
    }
  };

  const handleEditScac = async (newScac: string) => {
    let editOk = true;
    if (newScac === '' || newScac === null) {
      setScac(state.manifest.scacCode);
      dispatch({
        type: types.UPDATE_SCAC_COMPLETE,
        results: state.manifest
      });
      return;
    }
    if (freighLoadDetailsRequired(state.manifest)) {
      editOk = await editFreightLoad(freightLoad?.trailerNumber, freightLoad?.scheduledShipDate, newScac);
    }
    const manifestId = parseInt(props.match.params.id, 10);
    const response = await outboundManifestService.updateTrailerLoadingDetails(manifestId, null, newScac, null);
    editOk = editOk && response.data && (!response.errors || response.errors.length === 0);
    if (editOk) {
      dispatch({
        type: types.UPDATE_SCAC_COMPLETE,
        results: response.data
      });
    } else if (response.errors.length > 0) {
      dispatch({
        type: types.UPDATE_SCAC_ERROR,
        errors: response.errors
      });
    }
  };

  const handleEditProNumber = (newPro: string) => {
    setEditingProNumber(false);
    editFreightLoadStop(newPro, freightLoadStop?.sealNumber);
  };

  const handleEditSealNumber = (newSeal: string) => {
    setEditingSealNumber(false);
    editFreightLoadStop(freightLoadStop?.proNumber, newSeal);
  };

  const editFreightLoadStop = async (newPro: string, newSeal: string) => {
    setEditingFreight(true);
    let editSucessful = true;
    const response = await outboundFreightLoadService.updateFreightLoadStop({
      freightLoadStopId: freightLoadStop?.id,
      generateBol: false,
      proNumber: newPro,
      sealNumber: newSeal
    });
    if (response.errors && response.errors.length > 0) {
      dispatch({
        type: types.UPDATE_FREIGHT_LOAD_ERROR,
        errors: response.errors
      });
      editSucessful = false;
    }
    if (editSucessful) {
      fetchFreightLoad(state.manifest.freightLoadId);
    }
    setEditingFreight(false);
    return editSucessful;
  };

  const handleEditShipBy = (newShipBy: Date) => {
    setEditingShipBy(false);
    editFreightLoad(freightLoad?.trailerNumber, newShipBy.toISOString(), freightLoad?.scac);
  };

  const editFreightLoad = async (loadTrailerNumber: string, scheduledShipDate: string, loadScac: string) => {
    setEditingFreight(true);
    let editSucessful = true;
    const response = await outboundFreightLoadService.updateFreightLoad({
      freightLoadId: state.manifest.freightLoadId?.toString(),
      generateBol: false,
      trailerNumber: loadTrailerNumber,
      scheduledShipDate,
      scac: loadScac
    });
    if (response.errors && response.errors.length > 0) {
      dispatch({
        type: types.UPDATE_FREIGHT_LOAD_ERROR,
        errors: response.errors
      });
      editSucessful = false;
    }
    if (editSucessful) {
      fetchFreightLoad(state.manifest.freightLoadId);
    }
    setEditingFreight(false);
    return editSucessful;
  };

  const handleEditLoadingLocation = async (newLocationId: string, newLocationLabel) => {
    const manifestId = parseInt(props.match.params.id, 10);
    const response = await outboundManifestService.updateTrailerLoadingDetails(
      manifestId,
      null,
      null,
      newLocationLabel
    );
    if (response.data && (!response.errors || response.errors.length === 0)) {
      dispatch({
        type: types.UPDATE_LOADING_LOCATION_COMPLETE,
        results: response.data
      });
    } else {
      dispatch({
        type: types.UPDATE_LOADING_LOCATION_ERROR,
        errors: response.errors
      });
    }
  };

  const toggleEditTrailerId = async () => {
    dispatch({
      type: types.TOGGLE_EDIT_TRAILER_ID
    });
  };

  const toggleEditScac = async () => {
    dispatch({
      type: types.TOGGLE_EDIT_SCAC
    });
  };

  const toggleEditLoadingLocation = async () => {
    dispatch({
      type: types.TOGGLE_EDIT_LOADING_LOCATION
    });
  };

  const updateFilterParams = (key: string, value: string) => {
    // Update filterParams if filterParams[key] is set or if there's a value to
    // set for filterParams[key]
    if (filterParams[key] || value) {
      const newFilterParams = {...filterParams};

      if (value) {
        newFilterParams[key] = value;
      } else {
        delete newFilterParams[key];
      }

      setFilterParams(newFilterParams);
    }
  };

  const displayTrailerId = () => {
    return (
      <span className="info-list__description">
        <label>{'Trailer #: '}</label>
        {shouldAllowEditing() || state.manifest?.shipMode !== FreightShipMode ? (
          <span className="trailer_loading_edit">
            <InlineEdit
              title={'trailer_id'}
              placeholderText={'Enter Trailer Id'}
              editing={state.editingTrailerId}
              value={state.manifest.trailerId}
              onToggleEdit={toggleEditTrailerId}
              onUpdate={handleEditTrailerId}
              textArea={false}
            />
          </span>
        ) : (
          <div>
            <span className="inline-edit" />
            <span>{state.manifest.trailerId}</span>
          </div>
        )}
      </span>
    );
  };

  const displayScac = () => {
    return (
      <span className="info-list__description">
        <label>{'SCAC: '}</label>
        {shouldAllowEditing() || state.manifest?.shipMode !== FreightShipMode ? (
          <span className="trailer_loading_edit">
            <InlineEdit
              title={'scac'}
              placeholderText={'Enter SCAC'}
              editing={state.editingScac}
              value={state.manifest.scacCode}
              onToggleEdit={toggleEditScac}
              onUpdate={handleEditScac}
              textArea={false}
            />
          </span>
        ) : (
          <div>
            <span className="inline-edit" />
            <span>{state.manifest.scacCode}</span>
          </div>
        )}
      </span>
    );
  };

  const displayDoorLocation = () => {
    return (
      <span className="info-list__description trailer_door_location">
        <div>
          <label>Door Location:</label>
          <span className="inline-edit" />
          <span>{state.manifest.doorLocation}</span>
        </div>
      </span>
    );
  };

  const displayMode = () => {
    return (
      <span className="info-list__description trailer_mode">
        <div>
          <label>Mode:</label>
          <span className="inline-edit" />
          <span>{state.manifest.shipMode}</span>
        </div>
      </span>
    );
  };

  const displayDestinationTag = () => {
    return (
      <span className="info-list__description max_width">
        <div>
          <label>Destination ID:</label>
          <span>{freightLoadStop?.destinationTag}</span>
        </div>
      </span>
    );
  };

  const displayProNumber = () => {
    return (
      <span className="info-list__description max_width">
        <label>Pro #:</label>
        {shouldAllowEditing() ? (
          <span className="trailer_loading_edit">
            <InlineEdit
              title={'pro_number'}
              placeholderText={'Enter Pro Number'}
              editing={editingProNumber}
              value={freightLoadStop?.proNumber}
              onToggleEdit={() => {
                setEditingProNumber(!editingProNumber);
              }}
              onUpdate={handleEditProNumber}
              textArea={false}
            />
          </span>
        ) : (
          <div>
            <span className="inline-edit" />
            <span>{freightLoadStop?.proNumber}</span>
          </div>
        )}
      </span>
    );
  };

  const displaySealNumber = () => {
    return (
      <span className="info-list__description max_width">
        <label>Seal #:</label>
        {shouldAllowEditing() ? (
          <span className="trailer_loading_edit">
            <InlineEdit
              title={'seal_number'}
              placeholderText={'Enter Seal Number'}
              editing={editingSealNumber}
              value={freightLoadStop?.sealNumber}
              onToggleEdit={() => {
                setEditingSealNumber(!editingSealNumber);
              }}
              onUpdate={handleEditSealNumber}
              textArea={false}
            />
          </span>
        ) : (
          <div>
            <span className="inline-edit" />
            <span>{freightLoadStop?.sealNumber}</span>
          </div>
        )}
      </span>
    );
  };

  const formatShipByDate = () => {
    if (freightLoad?.scheduledShipDate) {
      return new Date(freightLoad.scheduledShipDate).toDateString();
    } else if (shouldAllowEditing()) {
      return 'Enter Date';
    }
    return '';
  };

  const displayShipBy = () => {
    return (
      <span className="info-list__description max_width">
        <label>{'Ship by: '}</label>
        {!editingShipBy &&
          (shouldAllowEditing() ? (
            <span className="trailer_loading_edit">
              <span className="inline-edit" />
              <span>{formatShipByDate()}</span>
              {shouldAllowEditing() && (
                <a
                  className="btn flat"
                  title="Edit-trailer_ship_by"
                  onClick={() => {
                    setEditingShipBy(!editingShipBy);
                  }}
                >
                  <i className="fa fa-pencil" />
                </a>
              )}
            </span>
          ) : (
            <div>
              <span className="inline-edit" />
              <span>{formatShipByDate()}</span>
            </div>
          ))}
        {editingShipBy && (
          <span className="trailer_loading_edit">
            <DatePicker
              name="Ship by"
              placeholderText="Enter Date"
              dateFormat="MM/dd/yyyy"
              // eslint-disable-next-line no-extra-boolean-cast
              selected={!!freightLoad?.scheduledShipDate ? new Date(Date.parse(freightLoad?.scheduledShipDate)) : null}
              onChange={handleEditShipBy}
            />
            <a
              className="btn flat cancel"
              title="Cancel-trailer_ship_by"
              onClick={() => {
                setEditingShipBy(!editingShipBy);
              }}
            >
              <i className="fa fa-lg fa-times" />
            </a>
          </span>
        )}
      </span>
    );
  };

  const displayFreightDetails = () => {
    return (
      <div className="data-header">
        <ul className="info-list">
          {editingFreight && <Loader loading={editingFreight} />}
          <li className="info-list__item" style={{border: 'none', padding: 'none'}}>
            {displayMode()}
            {displayScac()}
            {displayDestinationTag()}
          </li>
          <li className="info-list__item">
            {displayProNumber()}
            {displaySealNumber()}
            {displayTrailerId()}
          </li>
          <li className="info-list__item">
            {displayDoorLocation()}
            {displayShipBy()}
          </li>
          {shouldShowDownloadButton(state) && (
            <li className="info-list__item">
              <h4 className="info-list__header">Documents:</h4>
              <p className="info-list__description">
                <span className="download-manifest-link">
                  <FileDownloadLink
                    href={`/api/v2/outbound_manifests/${state.manifest.id}/download.csv`}
                    onClick={downloadManifest}
                    text="Download Load CSV"
                  />
                </span>
              </p>
            </li>
          )}
        </ul>
        <div className="ship-address">{destination && <ShippingInfo destination={destination} />}</div>
      </div>
    );
  };

  const displayGenericManifestDetails = () => {
    return (
      <div className="data-header">
        <ul className="info-list">
          <li className="info-list__item" style={{border: 'none'}}>
            <h4 className="info-list__header">Trailer Details</h4>
            {displayScac()}
            {displayTrailerId()}
            {displayDoorLocation()}
          </li>
          <div>
            <li className="info-list__item">
              <h4 className="info-list__header">Load Details</h4>
              {showNumberOfShipments && (
                <p className="info-list__description">
                  <label>{'Total Shipments:'}</label>
                  <span data-testid="info-list__total_pieces">{shipmentsCount}</span>
                </p>
              )}
              <p className="info-list__description">
                <label>{'Total Units:'}</label>
                <span data-testid="info-list__total_pieces">{state.manifest.totalPieces}</span>
              </p>
              <p className="info-list__description">
                <label>{'Total Weight:'}</label>
                <span data-testid="info-list__total_weight">
                  {/* eslint-disable-next-line no-extra-boolean-cast */}
                  {`${!!state.manifest.totalWeight ? Number(state.manifest.totalWeight.toFixed(2)) : '0.00'} lbs`}
                </span>
              </p>
              <p className="info-list__description">
                <label>Load Created At:</label>
                <span>{formatTimeWithTimezone(props.selectedWarehouse.timezone, state.manifest.createdAt)}</span>
              </p>
              <p className="info-list__description">
                <label>Load Last Updated At:</label>
                <span>{formatTimeWithTimezone(props.selectedWarehouse.timezone, state.manifest.updatedAt)}</span>
              </p>
            </li>
          </div>
          {shouldShowDownloadButton(state) && (
            <li className="info-list__item">
              <h4 className="info-list__header">Documents:</h4>
              <p className="info-list__description">
                <span className="download-manifest-link">
                  <FileDownloadLink
                    href={`/api/v2/outbound_manifests/${state.manifest.id}/download.csv`}
                    onClick={downloadManifest}
                    text="Download Manifest CSV"
                  />
                </span>
              </p>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const displayDetails = () => {
    if (state.manifest?.shipMode === FreightShipMode) {
      return displayFreightDetails();
    } else {
      return displayGenericManifestDetails();
    }
  };

  const renderLpnContents = () => {
    if (state.manifest?.shipMode === FreightShipMode && manifestLpns) {
      return (
        <ManifestContents
          manifestLpns={manifestLpns}
          manifestLpnsCount={manifestLpnsCount}
          pageSize={PAGE_SIZE}
          currentManifestLpnsPage={currentManifestLpnsPage}
          handlePagination={(page) => setCurrentManifestLpnsPage(page)}
          setRefreshLpns={(value) => setRefreshLpns(value)}
          authenticityToken={authenticityToken}
          allowEditing={shouldAllowEditing()}
        />
      );
    }
  };

  const renderActionButtons = () => {
    return (
      <ManifestDetailActionButtons
        manifest={state.manifest}
        disableCompleteAction={completingManifest || cancellingManifest || isManifestEmpty()}
        disableCancelAction={completingManifest || cancellingManifest}
        onComplete={() => {
          setCompletingManifest(true);
          completeAndSubmitToCarrier();
        }}
        onCancel={() => toggleIsCancelModalOpen()}
        onManifestComplete={() => setShowCompletedModal(true)}
      />
    );
  };

  const isManifestEmpty = (): boolean => {
    if (state.manifest?.shipMode === FreightShipMode) {
      return manifestLpnsCount === 0 || state.manifest.reservationId === null;
    }
    return state.manifest.reservationId === null;
  };

  if (state.loadError) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          {outboundManifestService.processErrors(state.errors).map((err, idx) => (
            <span key={idx}>{err}</span>
          ))}
        </div>
      </div>
    );
  } else if (state.redirectPath) {
    return <Redirect to={state.redirectPath} />;
  }

  const enqueueBillOfLadingGeneration = async () => {
    const errs = [];
    try {
      setBillOfLadingIsGenerating(true);
      const response = await outboundFreightLoadService.enqueueBillOfLadingGeneration(
        state.manifest.freightLoadId.toString()
      );
      if (response.errors.length > 0) {
        errs.push(response.errors.map((e) => e.detail));
      }
    } catch (e) {
      errs.push(e.toString());
    } finally {
      if (errs.length > 0) {
        dispatch({
          type: types.REGENERATE_BOL_ERROR,
          errors: errs
        });
      }
    }
  };

  // TODO: VENCOM-552 implement this
  // eslint-disable-next-line
  const placeholderStub = () => {};

  return (
    <div className="container-fluid">
      <div id="shipment-list">
        {!state.isLoading && (
          <div className="breadcrumbs delivery header">
            <a href="/wh/outbound_manifests">Loads</a>
            <span className="navigation-separator">/</span>
            {state.manifest.id}
          </div>
        )}
        {(state.otherError || state.errors.length > 0) && (
          <div className="alert alert-danger" role="alert">
            {outboundManifestService.processErrors(state.errors).map((err, idx) => (
              <span key={idx}>{err}</span>
            ))}
          </div>
        )}
        {state.isLoading || completingManifest || cancellingManifest ? (
          <Loader loading={true} />
        ) : (
          <React.Fragment>
            <div className="row">
              <div className="col-sm-6">
                <h1>{`Load: ${state.manifest.id}`}</h1>
              </div>
              {state.manifest && state.manifest.status === ManifestStatus.open && renderActionButtons()}
              <CancelLoadModal
                loadId={props.match.params.id}
                isOpen={isCancelModalOpen}
                toggleModal={toggleIsCancelModalOpen}
                onCancel={() => {
                  setCancellingManifest(true);
                }}
              />
              {state.manifest &&
                state.manifest.status === ManifestStatus.open &&
                state.manifest?.shipMode === FreightShipMode && (
                  <CompleteManifestModal
                    authenticityToken={authenticityToken}
                    showModal={showCompletedModal}
                    toggleModal={onSave}
                    onComplete={completeManifest}
                    manifest={state.manifest}
                    scheduledShipDate={freightLoad?.scheduledShipDate}
                    scac={scac}
                    proNumber={proNumber}
                    trailerNumber={trailerNumber}
                    sealNumber={sealNumber}
                    destinationId={freightLoadStop?.destinationTag}
                    destination={destination}
                    palletsLoaded={manifestLpnsCount}
                    setScac={(value) => {
                      setScac((value || '').toUpperCase());
                    }}
                    saveScac={() => handleEditScac(scac)}
                    setProNumber={(value) => setProNumber(value)}
                    saveProNumber={() => handleEditProNumber(proNumber)}
                    setSealNumber={(value) => setSealNumber(value)}
                    saveSealNumber={() => handleEditSealNumber(sealNumber)}
                    setTrailerNumber={(value) => setTrailerNumber(value)}
                    saveTrailerNumber={() => handleEditTrailerId(trailerNumber)}
                  />
                )}
            </div>
            {displayDetails()}
            {state.manifest?.shipMode === FreightShipMode && freightLoad && (
              <ShipmentDocuments
                authenticityToken={authenticityToken}
                documentsService={documentsService}
                reservationId={null}
                batchId={null}
                stopId={freightLoad.loadStops[0]?.id}
                bolNumber={freightLoad.loadStops[0]?.bolNumber}
                enqueueBillOfLadingGeneration={enqueueBillOfLadingGeneration}
                billOfLadingIsGenerating={billOfLadingIsGenerating}
                enqueueSsccLabelRegeneration={() => true}
                shipmentStatus={null}
                ssccLabelsAreGenerating={false}
                showUploadButton={false}
                freightPackingListIsGenerating={false}
                displayPrintLpnLabelsButton={false}
                enqueueFreightPackingListRegeneration={() => true}
                enableFreightPackingListRefresh={false}
                checkLabelRequirementsForCompletion={placeholderStub}
              />
            )}
            {state.manifest?.shipMode !== FreightShipMode && (
              <div>
                <div className="shipment-filter">
                  <hr />
                  <ShipmentFilter
                    filterParams={filterParams}
                    allowedFilterKeys={[FilterKeys.ShipmentId]}
                    onFilterChange={updateFilterParams}
                  />
                </div>
                {shipmentsLoading && <Loader loading={shipmentsLoading} />}
                {shipmentsCount > PAGE_SIZE && (
                  <div className="row vertical-align pull-right space-below">
                    <PaginationButtons
                      currentPage={currentPage}
                      pageSize={PAGE_SIZE}
                      setCurrentPage={setCurrentPage}
                      shipmentsCount={shipmentsCount}
                    />
                  </div>
                )}
                <ShipmentTable
                  shipments={shipmentsForDisplay}
                  isFreightTrailerLoadingEnabled={false}
                  isFreightWorkflowEnabled={false}
                  isOmniEnabled={false}
                  columnsToHide={
                    state.manifest.status === ManifestStatus.open
                      ? ['orderId', 'status', 'carrier', 'shipBy', 'lpns']
                      : ['orderId', 'status', 'carrier', 'shipBy', 'lpns', 'remove']
                  }
                  showCarrierUnderPro={true}
                  enableSortableHeaders={shipmentsCount > 1}
                  onSortChanged={handleSortChanged}
                  onRemoveShipment={(shipment: Shipment) => {
                    removeShipment(shipment.id);
                  }}
                  setShowLpnContentsModal={() => {
                    return;
                  }}
                  setShipmentForLPN={() => {
                    return;
                  }}
                  selectedShipments={[]}
                  setSelectedShipments={() => {
                    return;
                  }}
                  disableSelectedShipments={true}
                  isFreightLoadGroupUIEnabled={false}
                />
              </div>
            )}
            {renderLpnContents()}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default ManifestDetail;
